import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatNoteDate, type SiteStatusSummary } from '../../domain/siteNote';
import { formatPenCountsDate, type SitePenCounts } from '../../domain/sitePenCounts';
import { downloadCsv, filenamePart } from '../../lib/csv';
import { siteLabel } from '../../lib/site-label';
import {
  siteGenerationLabels,
  type CustomerSiteCountryGroup,
  type CustomerSiteInsight,
} from '../customers/customer-insights';
import { describeCompleteness, isEmptySite } from './site-completeness';
import { siteStatusCsv } from './site-status-csv';
import { siteStatusTotals, type SiteStatusTotals } from './site-status-totals';

/**
 * A customer's sites as one table, like the customer site status sheets it
 * replaces (tidaltech/koi#8261): systems, pens, what is missing or extra,
 * open requests and the latest from the status log. Countries and regions
 * are rows with their own totals that open to show their sites. Sites with
 * nothing ordered, on site or to bring back are hidden unless asked for.
 */
export function SiteStatusTable({
  groups,
  statusBySite,
  pensBySite,
  openRequestsBySite,
  expandAll = false,
  exportName,
}: {
  groups: readonly CustomerSiteCountryGroup[];
  statusBySite: ReadonlyMap<string, SiteStatusSummary>;
  pensBySite: { get(siteId: string): SitePenCounts | undefined };
  openRequestsBySite: ReadonlyMap<string, number>;
  /**
   * Start with every region open and empty sites shown, e.g. while a search
   * narrows the sites.
   */
  expandAll?: boolean;
  /** Names the exported file, e.g. the customer: "mowi-sites-2026-10-27.csv". */
  exportName?: string;
}) {
  const [showEmpty, setShowEmpty] = useState(expandAll);
  const isShown = (insight: CustomerSiteInsight) =>
    showEmpty || !isEmptySite(insight.site, insight.completeness);
  const hiddenCount = groups
    .flatMap((country) => country.regions.flatMap((region) => region.sites))
    .filter((insight) => !isShown(insight)).length;
  // Regions and countries left without sites disappear too.
  const visible = groups
    .map((country) => ({
      ...country,
      regions: country.regions
        .map((region) => ({ ...region, sites: region.sites.filter(isShown) }))
        .filter((region) => region.sites.length > 0),
    }))
    .filter((country) => country.regions.length > 0);
  const allKeys = groups.flatMap((country) => [
    country.key,
    ...country.regions.map((region) => region.key),
  ]);
  // Countries start open and regions closed: an overview by region.
  const [open, setOpen] = useState<Set<string>>(
    () => new Set(expandAll ? allKeys : groups.map((country) => country.key)),
  );
  const toggle = (key: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const totals = (sites: readonly CustomerSiteInsight[]) =>
    siteStatusTotals(sites, pensBySite, openRequestsBySite, statusBySite);
  const allSites = visible.flatMap((country) => country.regions.flatMap((region) => region.sites));

  return (
    <>
      <div className="site-status-table-actions">
        <button type="button" onClick={() => setOpen(new Set(allKeys))}>
          Expand all
        </button>
        <button type="button" onClick={() => setOpen(new Set())}>
          Collapse all
        </button>
        <button
          type="button"
          className="site-status-export"
          disabled={visible.length === 0}
          onClick={() =>
            downloadCsv(
              `${filenamePart(exportName ?? 'site-status')}-sites-${new Date().toISOString().slice(0, 10)}.csv`,
              siteStatusCsv(visible, totals, pensBySite, openRequestsBySite, statusBySite),
            )
          }
        >
          Export CSV
        </button>
        <label className="site-status-show-empty">
          <input
            type="checkbox"
            checked={showEmpty}
            onChange={(event) => setShowEmpty(event.target.checked)}
          />
          Show sites with nothing ordered or on site
          {!showEmpty && hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}
        </label>
      </div>
      {visible.length === 0 ? (
        <p className="empty-note">
          No site has anything ordered or on site. Tick “Show sites with nothing ordered or on site”
          to see them all.
        </p>
      ) : null}
      <div className="table-wrap site-status-table">
        <table>
          <thead>
            <tr>
              <th scope="col">Site</th>
              <th scope="col" title="Gen 1, Gen 2 or both, from the units on the site">
                Generation
              </th>
              <th scope="col" className="numeric-cell" title="Systems ordered for the site">
                Ordered
              </th>
              <th
                scope="col"
                className="numeric-cell"
                title="Complete systems on the site: the fewest of cameras, winches and unit controllers/powerboxes. Spares show under Missing / extra."
              >
                Systems
              </th>
              <th
                scope="col"
                className="numeric-cell"
                title="Systems running: the fewest of active cameras, winches and unit controllers/powerboxes, at most Systems"
              >
                Active
              </th>
              <th
                scope="col"
                className="numeric-cell"
                title="From the farm's data: pens with fish at the end of the day"
              >
                Pens with fish
              </th>
              <th
                scope="col"
                className="numeric-cell"
                title="Pens our systems sent data from that day or the day before"
              >
                Producing data
              </th>
              <th scope="col">Missing / extra</th>
              <th scope="col" className="numeric-cell">
                Open requests
              </th>
              <th scope="col">Last contact</th>
              <th scope="col">Latest note</th>
            </tr>
          </thead>
          <tbody>
            {visible.length > 1 ? (
              <TotalsRow level="all" label="All sites" totals={totals(allSites)} />
            ) : null}
            {visible.flatMap((country) => {
              const countryOpen = open.has(country.key);
              const countrySites = country.regions.flatMap((region) => region.sites);
              return [
                <TotalsRow
                  key={country.key}
                  level="country"
                  label={country.label}
                  totals={totals(countrySites)}
                  expanded={countryOpen}
                  onToggle={() => toggle(country.key)}
                />,
                ...(countryOpen
                  ? country.regions.flatMap((region) => {
                      const regionOpen = open.has(region.key);
                      return [
                        <TotalsRow
                          key={region.key}
                          level="region"
                          label={region.label}
                          totals={totals(region.sites)}
                          expanded={regionOpen}
                          onToggle={() => toggle(region.key)}
                        />,
                        ...(regionOpen
                          ? region.sites.map((insight) => (
                              <SiteRow
                                key={insight.site.id}
                                insight={insight}
                                pens={pensBySite.get(insight.site.id)}
                                status={statusBySite.get(insight.site.id)}
                                openRequests={openRequestsBySite.get(insight.site.id) ?? 0}
                              />
                            ))
                          : []),
                      ];
                    })
                  : []),
              ];
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function TotalsRow({
  level,
  label,
  totals,
  expanded,
  onToggle,
}: {
  level: 'all' | 'country' | 'region';
  label: string;
  totals: SiteStatusTotals;
  expanded?: boolean;
  onToggle?: () => void;
}) {
  const sites = `${totals.sites} ${totals.sites === 1 ? 'site' : 'sites'}`;
  return (
    <tr className={`site-status-group site-status-${level}`}>
      <th scope="row">
        {onToggle ? (
          <button
            type="button"
            className="site-status-toggle"
            aria-expanded={expanded}
            onClick={onToggle}
          >
            <span aria-hidden="true">{expanded ? '▾' : '▸'}</span> {label}
          </button>
        ) : (
          label
        )}
        <span className="customer-site-group-count">{sites}</span>
      </th>
      <td />
      <td className="numeric-cell">{totals.ordered ?? '—'}</td>
      <td className="numeric-cell">{totals.systems}</td>
      <td className="numeric-cell">{totals.active}</td>
      <td className="numeric-cell">{totals.pensWithFish ?? '—'}</td>
      <td className="numeric-cell">{totals.pensWithData ?? '—'}</td>
      <td>
        {totals.sitesWithGaps > 0
          ? `${totals.sitesWithGaps} ${totals.sitesWithGaps === 1 ? 'site' : 'sites'} with gaps`
          : '—'}
      </td>
      <td className="numeric-cell">{totals.openRequests}</td>
      <td>{totals.lastContact ? formatNoteDate(totals.lastContact) : '—'}</td>
      <td />
    </tr>
  );
}

function SiteRow({
  insight: { site, cameraSystems, completeness, generation },
  pens,
  status,
  openRequests,
}: {
  insight: CustomerSiteInsight;
  pens: SitePenCounts | undefined;
  status: SiteStatusSummary | undefined;
  openRequests: number;
}) {
  const sitePath = `/customers/${encodeURIComponent(site.customerId)}/sites/${encodeURIComponent(site.id)}`;
  const gaps = describeCompleteness(completeness);
  return (
    <tr className="site-status-site">
      <th scope="row">
        <Link to={sitePath}>{siteLabel(site)}</Link>
      </th>
      <td>
        {generation === 'unclassified' ? (
          '—'
        ) : (
          <span className={`generation-badge generation-${generation}`}>
            {siteGenerationLabels[generation]}
          </span>
        )}
      </td>
      <td className="numeric-cell">{site.orderedSystems ?? '—'}</td>
      <td
        className="numeric-cell"
        title={`${cameraSystems.assigned} camera${cameraSystems.assigned === 1 ? '' : 's'} assigned`}
      >
        {completeness.assembled}
      </td>
      <td
        className="numeric-cell"
        title={`${cameraSystems.active} active camera${cameraSystems.active === 1 ? '' : 's'}`}
      >
        {completeness.active}
      </td>
      <td
        className="numeric-cell"
        title={pens ? `On ${formatPenCountsDate(pens.date)}` : undefined}
      >
        {pens ? pens.pensWithFish : '—'}
      </td>
      <td className="numeric-cell">{pens ? pens.pensWithData : '—'}</td>
      <td className="site-status-gaps">
        {gaps.length > 0 ? (
          gaps.map((line) => <span key={line}>{line}</span>)
        ) : completeness.expected > 0 ? (
          <span className="site-status-complete">Complete</span>
        ) : (
          '—'
        )}
      </td>
      <td className="numeric-cell">
        {openRequests > 0 ? <Link to={`${sitePath}?tab=work`}>{openRequests}</Link> : 0}
      </td>
      <td>{status?.lastContact ? formatNoteDate(status.lastContact.createTime) : '—'}</td>
      <td className="site-status-note">
        {status?.latest ? (
          <>
            <span className="site-status-by">{formatNoteDate(status.latest.createTime)}:</span>{' '}
            {status.latest.text}
          </>
        ) : (
          '—'
        )}
      </td>
    </tr>
  );
}
